import React from 'react';
import CustomMedia from 'util/custom-media-query';
import {
  applyStyles,
  convertDoubleWidthNumber,
  convertKana,
  convertDoubleWidthAlpha,
  convertDoubleWidthSymbol,
  replaceHyphenAndSpaces,
  allowExactKeysOnly
} from 'util/helpers';
import i18n from 'i18n';
import BlockHeader from 'components/common/BlockHeader';
import TextField from 'components/common/TextField';
import {
  required,
  mustBeNumber,
  kanaPattern,
  emailPattern,
  composeValidators
} from 'validation/validators';
import SelectField from 'components/common/SelectField';
import { css } from 'styled-components';
import FontStyles from 'constants/font-styles';
import { get } from 'lodash';

const handleBlurNumbers = (change, key, value) => {
  const replaced = convertDoubleWidthNumber(value);
  const formatted = replaceHyphenAndSpaces(replaced);

  change(key, formatted);
};

const handleBlurKana = (change, value) => {
  const replaced = convertKana(value);

  change('customer.nameKana', replaced);
};

const handleBlurEmail = (change, value) => {
  const replacedNumber = convertDoubleWidthNumber(value);
  const replacedAlpha = convertDoubleWidthAlpha(replacedNumber);
  const replaced = convertDoubleWidthSymbol(replacedAlpha);

  change('customer.email', replaced);
};

const createSelectShipperOptions = orders => [
  {
    label: i18n.t('orderAggregators:common:order.select'),
    value: ''
  },
  ...orders.map((_, index) => ({
    label: `${i18n.t('orderAggregators:common:order.order')}${index +
      1}${i18n.t('orderAggregators:common:order.copyShipperValueLabel')}`,
    value: index
  }))
];

const copyShipperInfo = (change, index, orders) => {
  if (index === '') return;
  const shipper = orders[+index].shipper;
  const customerKeys = ['name', 'nameKana', 'telephone'];
  const mappingShipperKeys = {
    name: 'shipperName',
    nameKana: 'shipperNameKana',
    telephone: 'shipperTelephone'
  };

  return customerKeys.map(key =>
    change(`customer.${key}`, get(shipper, mappingShipperKeys[key]))
  );
};

const Customer = ({ className, change, orders }) => (
  <div className={className}>
    <BlockHeader>
      {i18n.t('orderAggregators:payment:customer.header')}
    </BlockHeader>

    <SelectField
      name="customer.copyShipperValue"
      label={i18n.t('orderAggregators:common:order.copyShipperLabel')}
      options={createSelectShipperOptions(orders)}
      onChange={e => copyShipperInfo(change, e.target.value, orders)}
    />

    <TextField
      name="customer.name"
      validate={value => composeValidators(required)(value)}
      label={i18n.t('orderAggregators:payment:customer.name')}
      labelNote={i18n.t('orderAggregators:payment:customer.nameLabelNote')}
      maxLength={30}
      placeholder={i18n.t('orderAggregators:payment:customer.namePlaceholder')}
      required={true}
      notes={[i18n.t('orderAggregators:payment:customer.nameNote1')]}
      autoComplete="name"
    />

    <TextField
      name="customer.nameKana"
      validate={value => kanaPattern(value)}
      label={i18n.t('orderAggregators:payment:customer.nameKana')}
      maxLength={30}
      placeholder={i18n.t(
        'orderAggregators:payment:customer.nameKanaPlaceholder'
      )}
      notes={[i18n.t('orderAggregators:payment:customer.nameKanaNote1')]}
      onBlur={value => handleBlurKana(change, value)}
    />

    <TextField
      type="tel"
      name="customer.telephone"
      validate={value => composeValidators(required, mustBeNumber)(value)}
      label={i18n.t('orderAggregators:payment:customer.telephone')}
      maxLength={12}
      placeholder={i18n.t(
        'orderAggregators:payment:customer.telephonePlaceholder'
      )}
      required={true}
      onBlur={value => handleBlurNumbers(change, 'customer.telephone', value)}
      onKeyDown={event => allowExactKeysOnly(event, /[^-]/)}
      autoComplete="tel"
    />

    <TextField
      name="customer.email"
      validate={value => composeValidators(required, emailPattern)(value)}
      label={i18n.t('orderAggregators:payment:customer.email')}
      maxLength={255}
      placeholder={i18n.t('orderAggregators:payment:customer.emailPlaceholder')}
      onBlur={value => handleBlurEmail(change, value)}
      required={true}
      autoComplete="email"
    />
  </div>
);

const commonStyle = css`
  ${SelectField} {
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-end;
    align-items: center;

    select {
      width: 148px;
    }

    label {
      ${FontStyles.text7};

      margin: 0 0 0 8px;
    }
  }
`;

const pcStyle = CustomMedia.greaterThan('umedium')`
  ${BlockHeader} {
    margin-top: 0;
  }
`;

const spStyle = CustomMedia.lessThan('medium')`
  ${BlockHeader} {
    margin: 32px 16px 24px;
  }
`;

export default applyStyles(Customer)(commonStyle, pcStyle, spStyle);



// WEBPACK FOOTER //
// ./src/components/OrderAggregators/Payment/components/Customer.js